import cloneDeep from "lodash/cloneDeep";
import { v4 as uid } from "uuid";

/* -----------------------------------------
 * TipTap / ProseMirror diff → merged document (configurable)
 * (Myers O(ND) for inline/token diffs)
 * -----------------------------------------
 * - Produces ONE merged doc (based on "after") that also shows deletions and insertions compared to the before version.
 * - Tables: shows deleted/inserted rows and cells if configured.
 * - Atomic nodes can opt-in to text-diff some fields (e.g., image.caption).
 *
 * Conventions (render-time):
 *  - Node attribute  : attrs["data-diff-node"] = "ins" | "del" | "changed"
 *  - Node attribute  : attrs[insDelIdAttr] = "<uuid-v4>"   (only for whole node ins/del)
 *  - Inline marks    : "ins" | "del" for content insert/delete; "chg" for formatting-only change
 */

/* ===================== DEFAULT CONFIG ===================== */

export const defaultConfig = {
    nodes: {
        doc: { textual: false, compareAttrs: [] },

        paragraph: { textual: true, compareAttrs: ["textAlign"] },
        heading: { textual: true, compareAttrs: ["level", "textAlign"] },

        bulletList: { textual: false, compareAttrs: [], aliases: ["orderedList"] },
        orderedList: { textual: false, compareAttrs: ["start"], aliases: ["bulletList"] },
        listItem: { textual: true, compareAttrs: [] },

        blockquote: { textual: true, compareAttrs: [] },

        table: { textual: false, compareAttrs: [] },
        tableRow: { textual: false, compareAttrs: [] },
        tableCell: { textual: true, compareAttrs: [], aliases: ["tableHeader"] },
        tableHeader: { textual: true, compareAttrs: [], aliases: ["tableCell"] },

        image: {
            atom: true,
            compareAttrs: ["src", "alt", "title"],
            atomic: { textFields: { caption: "words" } },
        },

        text: { textual: true, compareAttrs: [] },
    },

    marks: {
        bold: { formatting: true },
        italic: { formatting: true },
        underline: { formatting: true },
        strike: { formatting: true },
        code: { formatting: true },
        link: { formatting: true, compareAttrs: ["href", "target", "rel", "class"] },

        ins: { diff: true, attrs: {} },
        del: { diff: true, attrs: {} },
        chg: { diff: true, attrs: {} },
    },

    // node-level behavior
    treatAliasesAsSame: true,
    considerOrderForLists: true,

    // atomic nodes
    atom: {
        // Per node, opt-in comparison of text-like fields (e.g., image.caption)
        // Modes: "chars" | "words" | "none"
    },

    // bubbling flags
    bubbleChildChanges: true,
    bubbleFormattingOnlyChanges: true,
    flagDocWhenChildrenChange: false,
    flagDocWhenAnythingChanges: true, // ← mark doc changed if ANY change exists

    // knobs
    similarityThreshold: 0.6,
    maxDiffDepth: 100,
    enableEnhancedSimilarity: true,
    cleanupEmptyMarksForUnchanged: true,

    insDelIdAttr: "data-diff-id",
    makeUuid: uid,

    debug: false,
};

/* =========
 * PUBLIC API
 * ========= */

export function diffMerge(before, after, config) {
    const cfg = finalizeConfig(config || {});
    validateDocs(before, after);
    const out = mergeDoc(before, after, cfg);

    if (cfg.flagDocWhenAnythingChanges) {
        const anyChange = subtreeHasAnyChange(out, cfg);
        if (anyChange) {
            setDiffNode(out, "changed");
        }
    }
    return out;
}

/* ===================== CORE ===================== */

function validateDocs(a, b) {
    if (!a || !b || a.type !== "doc" || b.type !== "doc") {
        throw new Error('Both inputs must be TipTap/ProseMirror docs with type "doc".');
    }
}

function mergeDoc(a, b, config) {
    const out = { type: "doc", content: [] };

    // attrs: if doc attrs compared, keep after's
    const docDef = config.nodes.doc || {};
    if (docDef && docDef.compareAttrs && docDef.compareAttrs.length) {
        out.attrs = cloneDeep(b.attrs || {});
    }

    out.content = mergeChildren(a.content || [], b.content || [], config, 0);

    if (shouldBubbleChildrenChange(out.content || [], config)) {
        setDiffNode(out, "changed");
    }

    return out;
}

function mergeNode(a, b, config, depth = 0) {
    if (a.type !== b.type && !areAliases(a.type, b.type, config)) {
        const aDef = config.nodes[a.type] || {};
        const bDef = config.nodes[b.type] || {};
        // If both are textual blocks, treat as a changed node of the "after" type
        if (aDef.textual && bDef.textual) {
            const merged = { type: b.type, attrs: b.attrs ? { ...b.attrs } : undefined };
            merged.content = mergeChildren(a.content || [], b.content || [], config, depth + 1);
            setDiffNode(merged, "changed");
            return merged;
        }
        // Otherwise, treat as replacement
        return wrapAsGroup([markNodeDeleted(a, config), markNodeInserted(b, config)]);
    }

    const def = config.nodes[b.type] || {};
    const textual = !!def.textual;

    // Compare attrs
    const compareAttrs = new Set(def.compareAttrs || []);
    const sameAttrs = shallowEqualFiltered(a.attrs || {}, b.attrs || {}, compareAttrs);

    if (textual) {
        if (!sameAttrs) {
            const merged = cloneDeep(b);
            setDiffNode(merged, "changed");
            merged.content = mergeChildren(a.content || [], b.content || [], config, depth + 1);
            return merged;
        }
        const merged = { type: b.type, attrs: b.attrs ? { ...b.attrs } : undefined };
        merged.content = mergeChildren(a.content || [], b.content || [], config, depth + 1);

        if (shouldBubbleChildrenChange(merged.content || [], config)) {
            setDiffNode(merged, "changed");
        }

        return merged;
    }

    // Non-textual
    if (def.atom) {
        return mergeAtom(a, b, config);
    }
    if (b.type === "table") {
        return mergeTable(a, b, config);
    }

    const out = { type: b.type, attrs: b.attrs ? { ...b.attrs } : undefined };
    out.content = mergeChildren(a.content || [], b.content || [], config, depth + 1);

    // NEW: mark container as changed if compared attrs differ
    if (!sameAttrs) {
        setDiffNode(out, "changed");
    } else if (shouldBubbleChildrenChange(out.content || [], config)) {
        setDiffNode(out, "changed");
    }

    return out;
}

function mergeChildren(aKids, bKids, config, depth = 0) {
    if (depth > (config.maxDiffDepth || 100)) {
        console.warn(
            `Max diff depth (${config.maxDiffDepth || 100}) exceeded, treating as replacement`,
        );
        return bKids.map((kid) => cloneDeep(kid));
    }

    // paragraph ↔ paragraph fast path (single text child each)
    if (
        aKids.length === 1 &&
        bKids.length === 1 &&
        aKids[0].type === "paragraph" &&
        bKids[0].type === "paragraph"
    ) {
        const aHas = !!(aKids[0].content && aKids[0].content.length);
        const bHas = !!(bKids[0].content && bKids[0].content.length);

        if (
            aHas &&
            bHas &&
            aKids[0].content?.length === 1 &&
            aKids[0].content[0].type === "text" &&
            bKids[0].content?.length === 1 &&
            bKids[0].content[0].type === "text"
        ) {
            const aText = aKids[0].content[0].text || "";
            const bText = bKids[0].content[0].text || "";
            const aMarks = aKids[0].content[0].marks || [];
            const bMarks = bKids[0].content[0].marks || [];

            const diffedText = diffInlineText(aText, bText, aMarks, bMarks, config);
            const paragraph = {
                type: "paragraph",
                attrs: cloneDeep(bKids[0].attrs),
                content: diffedText,
            };

            const paraDef = config.nodes.paragraph || {};
            const cmp = new Set(paraDef.compareAttrs || []);
            const sameParaAttrs = shallowEqualFiltered(
                aKids[0].attrs || {},
                bKids[0].attrs || {},
                cmp,
            );
            if (!sameParaAttrs) {
                setDiffNode(paragraph, "changed");
            }

            // If only formatting changed, ensure there's a 'chg' mark (and DO NOT do this otherwise)
            if (aText === bText && !marksEqual(aMarks, bMarks, config)) {
                const firstText = (paragraph.content || []).find((n) => n.type === "text");
                if (firstText) {
                    firstText.marks = addChangeMark(firstText.marks || [], config);
                }
            }

            const hasChanges = paragraph.content.some(
                (n) =>
                    n.marks &&
                    n.marks.some(
                        (m) =>
                            m.type === "ins" ||
                            m.type === "del" ||
                            (config.bubbleFormattingOnlyChanges && m.type === "chg"),
                    ),
            );

            if (hasChanges) {
                setDiffNode(paragraph, "changed");
            }
            return [paragraph];
        }

        if (!aHas && bHas) {
            // inserted content
            const paragraph = cloneDeep(bKids[0]);
            const ins = markNodeInserted(paragraph, config);
            return [ins];
        }

        if (aHas && !bHas) {
            // deleted content
            const paragraph = cloneDeep(aKids[0]);
            const del = markNodeDeleted(paragraph, config);
            return [del];
        }

        if (!aHas && !bHas) {
            return [cloneDeep(bKids[0])];
        }
    }

    // pure text runs
    if (allText(aKids) && allText(bKids)) {
        return diffTextRunsWithMarks(aKids, bKids, config);
    }

    const steps = alignBySimilarity(
        aKids,
        bKids,
        (x) => blockSignature(x, config),
        config.similarityThreshold || 0.6,
        config,
    );
    const out = [];

    // iterate in forward order (alignBySimilarity already returns forward steps)
    for (const step of steps) {
        if (step.kind === "match" && step.before && step.after) {
            const a = step.before;
            const b = step.after;

            // merge like-types
            if (a.type === "text" && b.type === "text") {
                const aText = a.text || "";
                const bText = b.text || "";
                if (aText === bText) {
                    const sameMarks = marksEqual(a.marks, b.marks, config);
                    const marks = sameMarks
                        ? normalizeMarksForUnchanged(b.marks || [], config)
                        : addChangeMark(b.marks || [], config); // ensure 'chg' on formatting-only changes

                    const node = { type: "text", text: bText };
                    if (marks !== undefined) {
                        node.marks = marks;
                    }
                    out.push(node);
                } else {
                    const diffed = diffInlineText(
                        aText,
                        bText,
                        a.marks || [],
                        b.marks || [],
                        config,
                    );
                    out.push(...diffed);
                }
                continue;
            }

            out.push(mergeNode(a, b, config, depth + 1));
            continue;
        } else if (step.kind === "insert" && step.after) {
            out.push(markNodeInserted(step.after, config));
        } else if (step.kind === "delete" && step.before) {
            out.push(markNodeDeleted(step.before, config));
        }
    }

    const cleaned = out.filter(
        (n) => !(n.attrs?.["data-diff-node"] === "inserted" && isEmptyParagraph(n)),
    );
    return cleaned;
}

/* ===================== TABLES ===================== */

function mergeTable(a, b, config) {
    const out = { type: "table", attrs: b.attrs ? { ...b.attrs } : undefined, content: [] };

    const aRows = (a.content || []).filter((n) => n.type === "tableRow");
    const bRows = (b.content || []).filter((n) => n.type === "tableRow");

    const rowSteps = alignBySimilarity(aRows, bRows, (x) => rowSignature(x), 0.6, config);

    for (const step of rowSteps) {
        if (step.kind === "match" && step.before && step.after) {
            const mergedRow = mergeRow(step.before, step.after, config);
            const changedRowAttrs = !shallowEqualFiltered(
                step.before.attrs || {},
                step.after.attrs || {},
                new Set(),
            );

            if (changedRowAttrs || shouldBubbleChildrenChange(mergedRow.content || [], config)) {
                setDiffNode(mergedRow, "changed");
            }
            out.content.push(mergedRow);
        } else if (step.kind === "insert" && step.after) {
            out.content.push(markNodeInserted(step.after, config));
        } else if (step.kind === "delete" && step.before) {
            out.content.push(markNodeDeleted(step.before, config));
        }
    }

    if (shouldBubbleChildrenChange(out.content || [], config)) {
        setDiffNode(out, "changed");
    }

    const childChanged = (out.content || []).some(
        (r) => hasDiffFlag(r) || subtreeHasInlineDiffMarks(r, config),
    );

    if (childChanged || shouldBubbleChildrenChange(out.content || [], config)) {
        setDiffNode(out, "changed");
    }
    return out;
}

function mergeRow(a, b, config) {
    const out = { type: "tableRow", attrs: b.attrs ? { ...b.attrs } : undefined, content: [] };
    const aCells = (a.content || []).filter(
        (n) => n.type === "tableCell" || n.type === "tableHeader",
    );
    const bCells = (b.content || []).filter(
        (n) => n.type === "tableCell" || n.type === "tableHeader",
    );

    const steps = alignBySimilarity(aCells, bCells, (x) => cellSignature(x, config), 0.55, config);

    for (const step of steps) {
        if (step.kind === "match" && step.before && step.after) {
            const mergedCell = mergeCell(step.before, step.after, config);
            const changedCellAttrs = !shallowEqualFiltered(
                step.before.attrs || {},
                step.after.attrs || {},
                new Set(),
            );

            if (changedCellAttrs || shouldBubbleChildrenChange(mergedCell.content || [], config)) {
                setDiffNode(mergedCell, "changed");
            }
            out.content.push(mergedCell);
        } else if (step.kind === "insert" && step.after) {
            out.content.push(markNodeInserted(step.after, config));
        } else if (step.kind === "delete" && step.before) {
            out.content.push(markNodeDeleted(step.before, config));
        }
    }
    return out;
}

function mergeCell(a, b, config) {
    const out = { type: b.type, attrs: b.attrs ? { ...b.attrs } : undefined, content: [] };

    const aKids = a.content || [];
    const bKids = b.content || [];

    const steps = alignBySimilarity(aKids, bKids, (x) => blockSignature(x, config), 0.55, config);

    // Check for replacing empty paragraph with something else in which case we
    // will have only insert operation and not a delete/insert pair
    if (
        steps.length === 2 &&
        steps[0].kind === "delete" &&
        steps[1].kind === "insert" &&
        steps[0].before.type === "paragraph" &&
        steps[1].after.type !== "paragraph" &&
        !steps[0].before.content
    ) {
        steps.shift();
    }

    for (const step of steps) {
        if (step.kind === "match" && step.before && step.after) {
            out.content.push(mergeNode(step.before, step.after, config));
        } else if (step.kind === "insert" && step.after) {
            out.content.push(markNodeInserted(step.after, config));
        } else if (step.kind === "delete" && step.before) {
            out.content.push(markNodeDeleted(step.before, config));
        }
    }

    // NEW: if the only difference is tableHeader ↔ tableCell (aliases), treat as formatting change
    if (a.type !== b.type && areAliases(a.type, b.type, config)) {
        setDiffNode(out, "changed");
    } else if (shouldBubbleChildrenChange(out.content || [], config)) {
        setDiffNode(out, "changed");
    }

    return out;
}

/* ===================== ATOMS ===================== */

function mergeAtom(a, b, config) {
    // If attrs differ (on compared keys), flag as changed
    const def = config.nodes[b.type] || {};
    const compareAttrs = new Set(def.compareAttrs || []);
    const sameAttrs = shallowEqualFiltered(a.attrs || {}, b.attrs || {}, compareAttrs);

    const out = cloneDeep(b);

    // text-fields diff within atom?
    const atomic = def.atomic || {};
    const textFields = atomic.textFields || {};

    let anyFieldChange = false;

    for (const key of Object.keys(textFields)) {
        const mode = textFields[key];
        const aVal = (a.attrs || {})[key] || "";
        const bVal = (b.attrs || {})[key] || "";

        if (typeof aVal === "string" && typeof bVal === "string") {
            if (aVal === bVal) {
                continue;
            }

            const diffed =
                mode === "words"
                    ? diffInlineTextWords(aVal, bVal, [], [], config)
                    : diffInlineText(aVal, bVal, [], [], config);

            // Expose as separate attr so renderer can compose a caption view
            out.attrs[`data-diff-${key}`] = diffed;
            anyFieldChange = true;
        }
    }

    if (!sameAttrs || anyFieldChange) {
        setDiffNode(out, "changed");
    }

    return out;
}

/* ===================== TOKENIZATION & HELPERS ===================== */

// Split into tokens that preserve words, whitespace, and punctuation as separate tokens
function tokenize(s) {
    if (!s) {
        return [];
    }
    // groups: [word chars]+ | whitespace+ | other single char
    const re = /([A-Za-z0-9_]+|\s+|[^\sA-Za-z0-9_])/g;
    const out = [];
    let m;
    while ((m = re.exec(s)) !== null) {
        out.push(m[0]);
    }

    return out;
}

function allText(nodes) {
    return (nodes || []).every((n) => n.type === "text");
}

function withIns(marks = [], config) {
    const base = normalizeMarksKeepAfterPlusChg(marks || [], config) || [];
    return [...base, { type: "ins" }];
}

function tokensFromTextNodes(nodes) {
    const out = [];
    for (const n of nodes || []) {
        if (n.type !== "text") {
            continue;
        }
        const toks = tokenize(n.text || "");
        for (const t of toks) {
            out.push({ text: t, marks: (n.marks || []).slice() });
        }
    }
    return out;
}

function coalesceAdjacentTextWithSameMarks(seq) {
    if (seq.length === 0) {
        return seq;
    }
    const out = [];
    const sameMarks = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);
    for (const n of seq) {
        const last = out[out.length - 1];
        if (last && last.type === "text" && n.type === "text" && sameMarks(last.marks, n.marks)) {
            last.text = (last.text || "") + (n.text || "");
        } else {
            out.push(n);
        }
    }
    return out;
}

// currently a no-op; keep hook to tweak del/ins adjacency if needed
function coalesceInlineSwaps(seq, _config) {
    return seq;
}

// loose = ignore diff marks when comparing equality of formatting
function marksEqualLoose(a = [], b = [], config) {
    return marksEqual(a, b, config);
}

/* ===================== MYERS O(ND) DIFF (core) ===================== */

function myersDiff(A, B, eq = (x, y) => x === y) {
    const N = A.length;
    const M = B.length;
    const MAX = N + M;

    // V_d[k] = furthest x on diagonal k = x - y, for edit distance d
    const V = { 1: 0 };
    const trace = [];

    for (let D = 0; D <= MAX; D++) {
        const Vd = {};
        for (let k = -D; k <= D; k += 2) {
            let x;
            if (k === -D || (k !== D && (V[k - 1] ?? -Infinity) < (V[k + 1] ?? -Infinity))) {
                // go "down" — insertion in A (consume from B)
                x = V[k + 1] ?? 0;
            } else {
                // go "right" — deletion from A (consume from A)
                x = (V[k - 1] ?? 0) + 1;
            }
            let y = x - k;

            // follow the "snake" (consume equals)
            while (x < N && y < M && eq(A[x], B[y])) {
                x++;
                y++;
            }
            Vd[k] = x;

            // reached end
            if (x >= N && y >= M) {
                trace.push(Vd);
                return backtrackMyers(A, B, trace, eq);
            }
        }
        trace.push(Vd);
        // carry forward state
        for (const k in Vd) {
            V[+k] = Vd[+k];
        }
    }
    return [];
}

// 1) backtrackMyers: replace the whole function with this
function backtrackMyers(A, B, trace, eq) {
    const N = A.length,
        M = B.length;
    let x = N,
        y = M;
    const ops = [];

    for (let D = trace.length - 1; D >= 0; D--) {
        if (D === 0) {
            break; // no edits left at D=0
        }
        const prevV = trace[D - 1]; // ← MUST use the previous frontier

        const k = x - y;
        const goDown =
            k === -D || (k !== D && (prevV[k - 1] ?? -Infinity) < (prevV[k + 1] ?? -Infinity));
        const prevK = goDown ? k + 1 : k - 1;
        const prevX = prevV[prevK] ?? 0;
        const prevY = prevX - prevK;

        // walk back along snake (equals)
        while (x > prevX && y > prevY) {
            ops.push({ type: "eq", aIndex: x - 1, bIndex: y - 1 });
            x--;
            y--;
        }

        // single edit step
        if (goDown) {
            // insertion (consumed from B)
            ops.push({ type: "ins", bIndex: y - 1 });
            y--;
        } else {
            // deletion (consumed from A)
            ops.push({ type: "del", aIndex: x - 1 });
            x--;
        }
    }

    // consume any remaining equals at the start
    while (x > 0 && y > 0 && eq(A[x - 1], B[y - 1])) {
        ops.push({ type: "eq", aIndex: x - 1, bIndex: y - 1 });
        x--;
        y--;
    }

    ops.reverse();
    return ops;
}

/* ===================== INLINE TEXT DIFF (Myers-backed) ===================== */

function diffInlineText(a, b, aMarks = [], bMarks = [], config) {
    if (a === b) {
        const sameMarks = marksEqual(aMarks || [], bMarks || [], config);
        const marks = sameMarks
            ? normalizeMarksForUnchanged(bMarks || [], config)
            : addChangeMark(bMarks || [], config); // ensure 'chg' on formatting-only change

        const node = { type: "text", text: b };
        if (marks !== undefined) {
            node.marks = marks;
        }
        return [node];
    }

    const A = tokenize(a);
    const B = tokenize(b);

    // single-token → fall back to char-level refinement
    if (A.length === 1 && B.length === 1 && !/\s/.test(A[0]) && !/\s/.test(B[0])) {
        return diffInlineTextChars(a, b, aMarks, bMarks, config);
    }

    const ops = myersDiff(A, B, (x, y) => x === y);
    const out = [];

    for (const op of ops) {
        if (op.type === "eq") {
            const tok = B[op.bIndex];
            const sameFmt = marksEqual(aMarks, bMarks, config);
            const marks = sameFmt
                ? normalizeMarksForUnchanged(bMarks, config)
                : addChangeMark(bMarks, config); // IMPORTANT: add 'chg' when only formatting changed

            const node = { type: "text", text: tok };
            if (marks !== undefined) {
                node.marks = marks;
            }
            out.push(node);
        } else if (op.type === "del") {
            out.push({ type: "text", text: A[op.aIndex], marks: [{ type: "del" }] });
        } else {
            out.push({ type: "text", text: B[op.bIndex], marks: withIns(bMarks, config) });
        }
    }

    const seq = coalesceAdjacentTextWithSameMarks(coalesceInlineSwaps(out, config));

    // Safety: if we produced no ins/del but strings differ, force a replacement pair
    if (a !== b && !coalescedHasInsDel(seq)) {
        return [
            { type: "text", text: a, marks: [...(aMarks || []), { type: "del" }] },
            { type: "text", text: b, marks: withIns(bMarks || [], config) },
        ];
    }

    return seq;
}

function diffInlineTextWords(a, b, aMarks = [], bMarks = [], config) {
    // Word-wise via tokenize (tokens already word+ws+punc)
    return diffInlineText(a, b, aMarks, bMarks, config);
}

function diffInlineTextChars(a, b, aMarks = [], bMarks = [], config) {
    const A = [...(a || "")];
    const B = [...(b || "")];

    // Quick wins for very common cases:
    if (b.startsWith(a) && a.length > 0) {
        const unchanged = {
            type: "text",
            text: a,
            ...(normalizeMarksForUnchanged(bMarks || [], config)
                ? { marks: normalizeMarksForUnchanged(bMarks || [], config) }
                : {}),
        };
        const ins = { type: "text", text: b.slice(a.length), marks: withIns(bMarks, config) };
        return coalesceAdjacentTextWithSameMarks([unchanged, ins]);
    }

    if (a.startsWith(b) && b.length > 0) {
        const unchanged = {
            type: "text",
            text: b,
            ...(normalizeMarksForUnchanged(bMarks || [], config)
                ? { marks: normalizeMarksForUnchanged(bMarks || [], config) }
                : {}),
        };
        const del = { type: "text", text: a.slice(b.length), marks: [{ type: "del" }] };

        return coalesceAdjacentTextWithSameMarks([unchanged, del]);
    }

    // NEW: general prefix/suffix middle compaction (yields up to 3 runs)
    const pref = commonPrefix(a, b);
    const suff = commonSuffix(a.slice(pref.length), b.slice(pref.length));

    if (pref.length + suff.length > 0 && pref.length + suff.length < Math.max(a.length, b.length)) {
        const midA = a.slice(pref.length, a.length - suff.length);
        const midB = b.slice(pref.length, b.length - suff.length);

        const out = [];
        if (pref.length) {
            const marks = normalizeMarksForUnchanged(bMarks || [], config);
            const node = { type: "text", text: pref };
            if (marks !== undefined) {
                node.marks = marks;
            }
            out.push(node);
        }
        if (midA.length) {
            out.push({ type: "text", text: midA, marks: [...(aMarks || []), { type: "del" }] });
        }
        if (midB.length) {
            out.push({ type: "text", text: midB, marks: withIns(bMarks, config) });
        }
        if (suff.length) {
            const marks = normalizeMarksForUnchanged(bMarks || [], config);
            const node = { type: "text", text: suff };
            if (marks !== undefined) {
                node.marks = marks;
            }
            out.push(node);
        }
        return coalesceAdjacentTextWithSameMarks(out);
    }

    // Fallback: generic Myers on chars
    const ops = myersDiff(A, B, (x, y) => x === y);
    const out = [];
    for (const op of ops) {
        if (op.type === "eq") {
            const marks = normalizeMarksForUnchanged(bMarks || [], config);
            const node = { type: "text", text: B[op.bIndex] };
            if (marks !== undefined) {
                node.marks = marks;
            }
            out.push(node);
        } else if (op.type === "del") {
            out.push({
                type: "text",
                text: A[op.aIndex],
                marks: [...(aMarks || []), { type: "del" }],
            });
        } else {
            out.push({ type: "text", text: B[op.bIndex], marks: withIns(bMarks, config) });
        }
    }
    return coalesceAdjacentTextWithSameMarks(out);
}

function diffTextRunsWithMarks(aRuns, bRuns, config) {
    const A = tokensFromTextNodes(aRuns); // [{text, marks}]
    const B = tokensFromTextNodes(bRuns);

    const ops = myersDiff(
        A.map((t) => t.text),
        B.map((t) => t.text),
        (x, y) => x === y,
    );

    const out = [];

    for (const op of ops) {
        if (op.type === "eq") {
            const aTok = A[op.aIndex];
            const bTok = B[op.bIndex];

            const sameFmt = marksEqualLoose(aTok.marks, bTok.marks, config);
            const marks = sameFmt
                ? normalizeMarksForUnchanged(bTok.marks || [], config)
                : addChangeMark(bTok.marks || [], config); // ensure 'chg'

            const node = { type: "text", text: bTok.text };

            if (marks !== undefined) {
                node.marks = marks;
            }

            out.push(node);
        } else if (op.type === "del") {
            out.push({ type: "text", text: A[op.aIndex].text, marks: [{ type: "del" }] });
        } else {
            const bTok = B[op.bIndex];
            out.push({ type: "text", text: bTok.text, marks: withIns(bTok.marks || [], config) });
        }
    }

    const seq = coalesceAdjacentTextWithSameMarks(coalesceInlineSwaps(out, config));

    const aFlat = (aRuns || []).map((n) => n.text || "").join("");
    const bFlat = (bRuns || []).map((n) => n.text || "").join("");

    if (aFlat !== bFlat && !coalescedHasInsDel(seq)) {
        return [
            { type: "text", text: aFlat, marks: [{ type: "del" }] },
            { type: "text", text: bFlat, marks: withIns([], config) },
        ];
    }

    return seq;
}

/* ===================== ALIGNMENT (blocks) ===================== */

function alignBySimilarity(aList, bList, signature, threshold, config) {
    // simple forward DP alignment with similarity threshold (block-level is tiny, DP is fine)
    const n = aList.length,
        m = bList.length;
    const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
    const parent = Array.from({ length: n + 1 }, () => Array(m + 1).fill("diag"));

    const aSig = aList.map(signature);
    const bSig = bList.map(signature);

    function sim(i, j) {
        const ai = aList[i],
            bj = bList[j];
        const aDef = config.nodes[ai.type] || {};
        const bDef = config.nodes[bj.type] || {};

        // 1) Atoms: force a match only when the atom types are the same (or aliases).
        if (
            aDef.atom &&
            bDef.atom &&
            (ai.type === bj.type || areAliases(ai.type, bj.type, config))
        ) {
            return 1;
        }

        if (aDef.textual && bDef.textual && ai.type === bj.type) {
            const aEmpty = !gatherText(ai);
            const bEmpty = !gatherText(bj);

            // treat empty ↔ non-empty paragraph as "related" enough
            if (aEmpty !== bEmpty) {
                return threshold;
            }
        }

        // 2) Everyone else: start from content/signature similarity, then lightly bias.
        let s = similarity(aSig[i], bSig[j], config);

        // small bonus for same (or alias) type — keeps alignment stable without overmatching
        if (ai.type === bj.type || areAliases(ai.type, bj.type, config)) {
            s = Math.min(1, s + 0.08);
        }

        // tiny bonus for textual↔textual so paragraphs/headings still prefer each other
        if (aDef.textual && bDef.textual) {
            s = Math.min(1, s + 0.04);
        }

        return s >= threshold ? s : 0;
    }

    for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= m; j++) {
            const scoreDiag = dp[i - 1][j - 1] + sim(i - 1, j - 1);
            const scoreUp = dp[i - 1][j];
            const scoreLeft = dp[i][j - 1];

            if (scoreDiag >= scoreUp && scoreDiag >= scoreLeft) {
                dp[i][j] = scoreDiag;
                parent[i][j] = "diag";
            } else if (scoreUp >= scoreLeft) {
                dp[i][j] = scoreUp;
                parent[i][j] = "up";
            } else {
                dp[i][j] = scoreLeft;
                parent[i][j] = "left";
            }
        }
    }

    // backtrack → steps, then reverse to forward order
    const steps = [];
    let i = n,
        j = m;
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && parent[i][j] === "diag" && sim(i - 1, j - 1) > 0) {
            steps.push({ kind: "match", before: aList[i - 1], after: bList[j - 1] });
            i--;
            j--;
        } else if (i > 0 && (j === 0 || parent[i][j] === "up")) {
            steps.push({ kind: "delete", before: aList[i - 1] });
            i--;
        } else {
            steps.push({ kind: "insert", after: bList[j - 1] });
            j--;
        }
    }

    return steps.reverse();
}

/* ===================== SIGNATURES & SIMILARITY ===================== */

function blockSignature(n, config) {
    const def = config.nodes[n.type] || {};
    const textual = !!def.textual;
    const atom = !!def.atom;

    if (textual) {
        // ignore type for textual nodes to allow paragraph <-> heading matches
        const text = gatherText(n);
        return "textual|" + text.toLowerCase().slice(0, 200);
    }

    let s = n.type;

    if (areAliasesNormalized(n.type, config)) {
        s = normalizeAliasType(n.type, config);
    }

    if (atom) {
        const compareAttrs = new Set(def.compareAttrs || []);
        const attrsStr = JSON.stringify(filterAttrs(n.attrs || {}, compareAttrs));
        s += "|" + attrsStr;
    } else {
        const childTypes = (n.content || [])
            .map((c) =>
                areAliasesNormalized(c.type, config) ? normalizeAliasType(c.type, config) : c.type,
            )
            .join(",");
        s += "|" + childTypes;
    }

    return s;
}

function rowSignature(row) {
    const cells = (row.content || []).filter(
        (n) => n.type === "tableCell" || n.type === "tableHeader",
    );
    const widths = cells.map((c) => (c.attrs && c.attrs.colspan) || 1).join(",");

    return "row|" + widths;
}

function cellSignature(cell, config) {
    const baseType = areAliasesNormalized(cell.type, config)
        ? normalizeAliasType(cell.type, config)
        : cell.type;
    const text = gatherText(cell).slice(0, 200).toLowerCase();

    return baseType + "|" + text;
}

function gatherText(n) {
    if (n.type === "text") {
        return n.text || "";
    }

    const kids = n.content || [];

    return kids.map(gatherText).join("");
}

function similarity(a, b, config) {
    if (a === b) {
        return 1;
    }

    if (!a || !b) {
        return 0;
    }

    if (config.enableEnhancedSimilarity) {
        const la = a.length,
            lb = b.length;
        const maxL = Math.max(la, lb);
        const prefix = commonPrefix(a, b).length / maxL;
        const suffix = commonSuffix(a, b).length / maxL;
        const j = jaccard(a, b);
        return 0.5 * j + 0.25 * prefix + 0.25 * suffix;
    }

    // fallback: jaccard of 3-grams
    return jaccard(a, b);
}

function jaccard(a, b) {
    const sa = new Set(ngrams(a, 3));
    const sb = new Set(ngrams(b, 3));
    const inter = new Set([...sa].filter((x) => sb.has(x)));
    const union = new Set([...sa, ...sb]);
    return union.size === 0 ? 0 : inter.size / union.size;
}

function ngrams(s, n) {
    if (!s) {
        return [];
    }

    if (s.length <= n) {
        return [s];
    }

    const out = [];

    for (let i = 0; i <= s.length - n; i++) {
        out.push(s.slice(i, i + n));
    }

    return out;
}

function commonPrefix(a, b) {
    let i = 0;
    while (i < a.length && i < b.length && a[i] === b[i]) {
        i++;
    }

    return a.slice(0, i);
}

function commonSuffix(a, b) {
    let i = 0;

    while (i < a.length && i < b.length && a[a.length - 1 - i] === b[b.length - 1 - i]) {
        i++;
    }

    return a.slice(a.length - i);
}

/* ===================== MARKS HELPERS ===================== */

function marksEqual(a = [], b = [], _config) {
    const an = (a || []).filter((m) => !isDiffMark(m));
    const bn = (b || []).filter((m) => !isDiffMark(m));

    if (an.length !== bn.length) {
        return false;
    }

    const sa = an.map(simpleMarkKey).sort().join("|");
    const sb = bn.map(simpleMarkKey).sort().join("|");

    return sa === sb;
}

function isDiffMark(m) {
    return m && (m.type === "ins" || m.type === "del" || m.type === "chg");
}

function simpleMarkKey(m) {
    if (!m) {
        return "";
    }

    const attrs = m.attrs ? JSON.stringify(m.attrs) : "";

    return m.type + ":" + attrs;
}

function normalizeMarksForUnchanged(marks = [], config) {
    const cleaned = (marks || []).filter((m) => !isDiffMark(m));

    if (config.cleanupEmptyMarksForUnchanged && cleaned.length === 0) {
        return undefined;
    }

    return cleaned;
}

function normalizeMarksKeepAfterPlusChg(marks = [], config) {
    // keep only non-diff AND "chg" (formatting change) if present
    const cleaned = (marks || []).filter((m) => m.type === "chg" || !isDiffMark(m));

    if (config.cleanupEmptyMarksForUnchanged && cleaned.length === 0) {
        return undefined;
    }

    return cleaned;
}

/* ===================== TREE FLAGS ===================== */

function shouldBubbleChildrenChange(children, config) {
    if (!config.bubbleChildChanges) {
        return false;
    }

    return (children || []).some((child) => subtreeHasAnyChange(child, config));
}

/* ===================== HELPERS ===================== */

function setDiffNode(n, kind) {
    n.attrs = n.attrs || {};
    // Short codes for attrs as required by tests (e.g., "ins" for tables)
    n.attrs["data-diff-node"] =
        kind === "inserted" ? "inserted" : kind === "deleted" ? "deleted" : kind;
}

function hasDiffFlag(n) {
    return !!(n && n.attrs && n.attrs["data-diff-node"]);
}

function wrapAsGroup(nodes) {
    return { type: "group", content: nodes.map(cloneDeep) };
}

function markNodeInserted(n, config) {
    const copy = cloneDeep(n);
    setDiffNode(copy, "inserted");
    ensureInsDelId(copy, config);

    return copy;
}

function markNodeDeleted(n, config) {
    const copy = cloneDeep(n);
    setDiffNode(copy, "deleted");
    ensureInsDelId(copy, config);

    return copy;
}

function ensureInsDelId(n, config) {
    n.attrs = n.attrs || {};
    const attr = config.insDelIdAttr || "data-diff-id";

    if (!n.attrs[attr]) {
        n.attrs[attr] = (config.makeUuid || defaultConfig.makeUuid)();
    }
}

function areAliases(a, b, config) {
    const na = normalizeAliasType(a, config);
    const nb = normalizeAliasType(b, config);

    return na === nb;
}

function areAliasesNormalized(a, config) {
    const def = config.nodes[a] || {};

    return !!(def.aliases && def.aliases.length);
}

function normalizeAliasType(t, config) {
    const def = config.nodes[t] || {};

    if (!def.aliases || !def.aliases.length) {
        return t;
    }

    if (config.treatAliasesAsSame) {
        const clan = [t, ...(def.aliases || [])].sort();
        return clan[0];
    }

    return t;
}

function addChangeMark(marks, config) {
    const base = normalizeMarksKeepAfterPlusChg(marks || [], config) || [];
    // ensure a single 'chg' exists
    const has = base.some((m) => m.type === "chg");
    return has ? base : [...base, { type: "chg" }];
}

function subtreeHasInlineDiffMarks(n, config) {
    if (!n) {
        return false;
    }

    if (n.type === "text") {
        const marks = n.marks || [];
        const hasInsDel = marks.some((m) => m.type === "ins" || m.type === "del");
        const hasChg = marks.some((m) => m.type === "chg");
        return hasInsDel || (config.bubbleFormattingOnlyChanges && hasChg);
    }

    return (n.content || []).some((c) => subtreeHasInlineDiffMarks(c, config));
}

function subtreeHasAnyChange(n, config) {
    // structural node flags OR inline marks
    if (hasDiffFlag(n)) {
        return true;
    }

    if (subtreeHasInlineDiffMarks(n, config)) {
        return true;
    }

    return (n.content || []).some((c) => subtreeHasAnyChange(c, config));
}

function coalescedHasInsDel(seq) {
    return (seq || []).some(
        (n) =>
            n.type === "text" && (n.marks || []).some((m) => m.type === "ins" || m.type === "del"),
    );
}

function isEmptyParagraph(n) {
    if (n.type !== "paragraph") {
        return false;
    }
    const txt = gatherText(n);

    return (txt || "").length === 0;
}

/* ===================== UTILITIES ===================== */

function shallowEqual(a = {}, b = {}) {
    const ka = Object.keys(a || {}),
        kb = Object.keys(b || {});
    if (ka.length !== kb.length) {
        return false;
    }
    for (const k of ka) {
        if (a[k] !== b[k]) {
            return false;
        }
    }
    return true;
}

function shallowEqualFiltered(a, b, keepSet = new Set()) {
    if (!keepSet || keepSet.size === 0) {
        return shallowEqual(a || {}, b || {});
    }
    const fa = filterAttrs(a, keepSet);
    const fb = filterAttrs(b, keepSet);
    return shallowEqual(fa, fb);
}

function filterAttrs(a, keepSet) {
    const out = {};
    for (const k of keepSet) {
        out[k] = (a || {})[k];
    }
    return out;
}

/* ===================== CONFIG FINALIZATION ===================== */

function finalizeConfig(cfg) {
    const merged = { ...defaultConfig, ...(cfg || {}) };

    merged.nodes = merged.nodes || {};
    const nodeProxy = new Proxy(merged.nodes, {
        get(target, prop) {
            if (prop in target) {
                return target[prop];
            }

            return { textual: false, compareAttrs: [] };
        },
    });
    merged.nodes = nodeProxy;

    // ensure table helpers exist
    if (!merged.nodes.tableRow) {
        merged.nodes.tableRow = { textual: false, compareAttrs: [] };
    }

    if (!merged.nodes.tableCell) {
        merged.nodes.tableCell = { textual: true, compareAttrs: [] };
    }

    if (!merged.nodes.tableHeader) {
        merged.nodes.tableHeader = { textual: true, compareAttrs: [] };
    }

    // ensure text mark proxies
    merged.marks = merged.marks || {};
    const markProxy = new Proxy(merged.marks, {
        get(target, prop) {
            if (prop in target) {
                return target[prop];
            }

            return { formatting: true };
        },
    });
    merged.marks = markProxy;

    merged.nodes = merged.nodes || {};
    return merged;
}
